const itemsList = require('../itemsList/itemsList');

// widgetsList extends itemsList — same AJAX loading, filters, actions and pagination,
// but renders each item as a window in a responsive grid instead of a table row
const widgetsList = Object.assign({}, itemsList, {
    subelements: Object.assign({}, itemsList.subelements, {
        // widgetsHolder replaces the table as the items container
        widgetsHolder: null
    }),

    // Grid span for each widget window — integer or object (breakpoint → span)
    itemSpan: 12,

    // Col alias whose content goes into the window header title; empty string = no title
    itemTitleCol: '',

    // Whether individual widget windows show a collapse toggle button
    itemCollapsible: false,

    // Which windows start collapsed: 'none' | 'all' | 'allButFirst' | 'allButLast'
    itemsInitiallyCollapsed: 'none',

    // Where per-item actions are rendered: 'header' (default) or 'footer'
    itemActionsPosition: 'header',

    // No-op: widgetsList has no actions column — per-item actions live in each window's header toolbar
    itemsActions: function (response) {
    },

    clearList: function () {
        // Remove all dynamically added widget windows from the holder
        while (this.subelements.widgetsHolder.firstChild) {
            this.subelements.widgetsHolder.removeChild(this.subelements.widgetsHolder.firstChild);
        }

        // Show loading spinner in the applied-filters area while new items are fetched
        if (this.subelements.appliedFilters !== false) {
            this.subelements.appliedFilters.innerHTML = '<div class="' + this.hm.app.cssClass('textSizeMedium') + ' zit-display-flex zit-justify-content-center">' + this.hm.app.utext('pleaseWait') + '... <span class="' + this.hm.app.cssClass('loadingSpinner') + ' zit-align-middle zit-ms-2"></span></div>';
        }

        this.removeSubcomponents();

        // Destroy sortable instance so it can be re-created after items reload
        if (this.enableSort && this.sortable !== false) {
            delete this.sortable;
            this.sortable = false;
        }

        this.dispatch('zitClear', {});
    },

    displayItems: function (response) {
        if (response.items.length > 0) {
            response.items.forEach((item, index) => {
                // Outer grid column — carries the item identity and optional draggable class
                let colDiv = document.createElement('div');
                colDiv.className = this.getItemSpanClasses() + (this.enableSort ? ' draggable' : '');
                colDiv.dataset.zitItemId = item.itemId;
                colDiv.dataset.zitIndex = index;

                // Determine whether this item has any per-item actions
                let hasItemActions = typeof item.actions !== typeof undefined &&
                    this.hm.helper.isObject(item.actions) &&
                    Object.getOwnPropertyNames(item.actions).length > 0;

                // Determine whether to render a window header
                let titleHtml = (this.itemTitleCol && item.components[this.itemTitleCol]) ? item.components[this.itemTitleCol] : '';
                let needsHeader = titleHtml || (this.itemActionsPosition === 'header' && hasItemActions) || this.enableSort || this.hasActions.selected || this.itemCollapsible;

                // Build item-window HTML. JS-rendered content has no per-component
                // zone-indirection layer — we look up cssClassesMap keys directly
                // via hm.app.cssClass. Keys: widgetsListWindow / widgetsListWindowHeader /
                // widgetsListWindowFooter carry widget-specific decoration; the
                // bare windowTitle / windowToolbar / windowBody keys are shared
                // with the outer window.
                let windowHtml = '<div class="' + this.hm.app.cssClass('widgetsListWindow') + '">';

                if (needsHeader) {
                    // widgetsListWindowHeader resolves to "card-header min-h-60px"
                    // under m8. windowCollapsible (m8 → "collapsible", zb5 → "")
                    // is static decoration marking "this header offers a collapse
                    // affordance" — looked up via cssClassesMap, not via behaviors
                    // (which owns the runtime state classes root/shown/hidden).
                    windowHtml += '<div class="' + this.hm.app.cssClass('widgetsListWindowHeader') + (this.itemCollapsible ? ' ' + this.hm.app.cssClass('windowCollapsible') : '') + '">';

                    // Title row: sort handle, selected checkbox, then widget title.
                    windowHtml += '<div class="' + this.hm.app.cssClass('windowTitle') + '">';
                    if (this.enableSort) {
                        // Drag handle icon — semantic name resolved from the theme's icon map.
                        // Button chrome composed from cssClassesMap atoms (was a hardcoded
                        // Metronic btn string — lifted in 7n); draggable-handle is the
                        // third-party Draggable selector, intentionally un-prefixed and
                        // outside the resolved chrome (see scripts/behaviors.contract.md
                        // "Documented exception").
                        windowHtml += '<a href="#" class="' + this.hm.app.cssClass('button') + ' ' + this.hm.app.cssClass('iconButton') + ' ' + this.hm.app.cssClass('actionSmall') + ' ' + this.hm.app.cssClass('buttonHoverLightPrimary') + ' draggable-handle">' +
                            '<i class="' + (this.hm.app.icons.dragHandle ?? '') + ' ' + this.hm.app.cssClass('iconSizeExtraLarge') + '"></i>' +
                            '</a>';
                    }
                    if (this.hasActions.selected) {
                        windowHtml += item.components.selected;
                    }
                    if (titleHtml) {
                        windowHtml += '<h3 class="zit-m-0 ' + this.hm.app.cssClass('windowHeadingText') + '">' + titleHtml + '</h3>';
                    }
                    windowHtml += '</div>';

                    // Toolbar zone: collapse toggle (if enabled) + per-item actions (when header position).
                    let actionsHtml = '';
                    if (this.itemCollapsible) {
                        // Collapse-toggle icon — uses chevronDown from the theme's icon map.
                        // Emits zit-namespaced markers; bridgeTierAttrs (called below
                        // after the window is appended) mirrors them onto data-bs-* so
                        // Bootstrap's collapse click handler picks them up.
                        // Button chrome composed from cssClassesMap atoms (was a
                        // hardcoded Metronic btn string — lifted in 7n).
                        actionsHtml += '<a href="#" class="' + this.hm.app.cssClass('button') + ' ' + this.hm.app.cssClass('iconButton') + ' ' + this.hm.app.cssClass('actionSmall') + ' ' + this.hm.app.cssClass('buttonActiveTextPrimary') + '"' +
                            ' data-zit-toggle="collapse" data-zit-collapse-target="#widgetsList_' + this.id + '_' + index + '">' +
                            '<i class="' + (this.hm.app.icons.chevronDown ?? '') + ' ' + this.hm.app.cssClass('iconSizeRegular') + '"></i>' +
                            '</a>';
                    }
                    if (this.itemActionsPosition === 'header') {
                        for (let actionName in item.actions) {
                            actionsHtml += item.actions[actionName];
                        }
                    }
                    windowHtml += '<div class="' + this.hm.app.cssClass('windowToolbar') + '">' + actionsHtml + '</div>';

                    windowHtml += '</div>'; // end window header
                }

                // Determine initial collapse state for this item's body.
                let totalItems = response.items.length;
                let showBody = !this.itemCollapsible ||
                    this.itemsInitiallyCollapsed === 'none' ||
                    (this.itemsInitiallyCollapsed === 'allButFirst' && index === 0) ||
                    (this.itemsInitiallyCollapsed === 'allButLast' && index === totalItems - 1);

                // Wrap body in a collapsible container when itemCollapsible is enabled.
                // State classes resolved via hm.behaviors.collapse.stateClass — keeps the
                // tier's literal tokens out of this JS.
                if (this.itemCollapsible) {
                    const collapse = this.hm.behaviors.collapse;
                    const rootClass = collapse.stateClass('root');
                    const visibilityClass = showBody ? collapse.stateClass('shown') : collapse.stateClass('hidden');
                    windowHtml += '<div id="widgetsList_' + this.id + '_' + index + '" class="' + rootClass + ' ' + visibilityClass + '">';
                }

                // Body: all cols except the one used as the header title,
                // laid out in a Bootstrap grid using each component's configured span.
                let componentSpans = item.componentSpans || {};
                windowHtml += '<div class="' + this.hm.app.cssClass('windowBody') + '"><div class="' + this.hm.app.cssClass('row') + ' ' + this.hm.app.cssClass('gap3') + '">';
                this.colsAliases.forEach((colAlias) => {
                    // Skip the title col — it is already shown in the header.
                    if (colAlias === this.itemTitleCol) {
                        return;
                    }
                    if (item.components[colAlias]) {
                        windowHtml += '<div class="' + this.getColSpanClasses(colAlias, componentSpans) + '">' + item.components[colAlias] + '</div>';
                    }
                });
                windowHtml += '</div></div>'; // end row, end window body

                // Footer with per-item actions when footer position is configured;
                // placed inside the collapsible wrapper so it hides/shows with the body.
                if (this.itemActionsPosition === 'footer' && hasItemActions) {
                    windowHtml += '<div class="' + this.hm.app.cssClass('widgetsListWindowFooter') + '">';
                    for (let actionName in item.actions) {
                        windowHtml += item.actions[actionName];
                    }
                    windowHtml += '</div>'; // end window footer
                }

                if (this.itemCollapsible) {
                    windowHtml += '</div>'; // end collapsible wrapper
                }

                windowHtml += '</div>'; // end window

                colDiv.innerHTML = windowHtml;
                this.subelements.widgetsHolder.appendChild(colDiv);

                // Bridge the inserted window's zit-namespaced markup onto the tier's
                // framework-namespaced equivalents (zb5 mirrors data-zit-toggle="collapse"
                // and data-zit-collapse-target onto data-bs-* for Bootstrap's collapse
                // click handler). Static markup is bridged once at boot in app.initTheme;
                // dynamic markup like these windows needs the per-subtree call.
                this.hm.behaviors.bridgeTierAttrs(colDiv);

                // Initialise any nested zit components rendered inside this window
                this.subComponents = this.subComponents.concat(this.hm.app.initComponents(colDiv));
            });
        } else {
            // Show "no items found" message spanning the full row
            let noItemsDiv = document.createElement('div');
            noItemsDiv.className = 'col-12';
            noItemsDiv.innerHTML = '<div class="' + this.hm.app.cssClass('textGrayMedium') + ' zit-fw-semibold ' + this.hm.app.cssClass('textSizeSmall') + ' zit-text-center zit-py-5">' +
                this.hm.app.utext('noItemsFound') + '</div>';
            this.subelements.widgetsHolder.appendChild(noItemsDiv);
        }
    },

    // Build Bootstrap column class for a specific component inside the widget window;
    // falls back to col-6 (the component base-class default) when no span is provided
    getColSpanClasses: function (colAlias, componentSpans) {
        let span = (componentSpans && componentSpans[colAlias] != null) ? componentSpans[colAlias] : 6;
        return 'col-' + span;
    },

    // Build Bootstrap column classes from itemSpan (integer or breakpoint→span object)
    getItemSpanClasses: function () {
        if (typeof this.itemSpan === 'object' && this.itemSpan !== null) {
            return Object.entries(this.itemSpan)
                .map(([bp, span]) => 'col' + (bp ? '-' + bp : '') + (span ? '-' + span : ''))
                .join(' ');
        }
        return 'col-' + this.itemSpan;
    },

    // Forward initSortable to the parent, passing widgetsHolder as the sortable container
    initSortable: function () {
        itemsList.initSortable.call(this, this.subelements.widgetsHolder);
    },

    // Override saveSort to query widget divs instead of <tr> elements
    saveSort: async function () {
        this.clearTimer('saveSort_' + this.id);

        let sorted = {};

        this.subelements.widgetsHolder.querySelectorAll('[data-zit-item-id]').forEach((div, index) => {
            let itemId = parseInt(div.dataset.zitItemId);
            let currentIndex = parseInt(div.dataset.zitIndex);

            if (itemId && currentIndex !== index) {
                div.dataset.zitIndex = index;
                sorted[itemId] = index;
            }
        });

        await this.submitSortOrder(sorted);
    }

});

module.exports = widgetsList;
